import { useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { deserializeUrl, serialize } from '../../utils/url-utils'
import { $businessUnit } from '../auth'
import { $urlContext } from './atoms'
import { UrlContext } from './types'

/**
 * The `useUrlContext` function returns an object with properties `context`, `update`, and `serialize`,
 * which can be used to manage and manipulate the URL context.
 * @returns an object with three properties: `context`, `update`, and `serialize`.
 */
export function useUrlContext() {
  const [urlContext, setUrlContext] = useRecoilState($urlContext)
  const buId = useRecoilValue($businessUnit)

  return useMemo(() => {
    function update(newContext: string): UrlContext {
      const deserialized = deserializeUrl(newContext)
      /**
       * Make sure the business unit id is always present in the context.
       * I'm not sure if the id from the token or the one in the url should take precedence.
       * As of now I'm letting the url win, this as to be checked once we start implementing emails
       * that point to a specific BU
       */
      if (!deserialized['BUSINESS_UNIT']) {
        deserialized['BUSINESS_UNIT'] = buId
      }
      setUrlContext(deserialized)
      return deserialized
    }

    function getTabContext(moduleName: string | undefined): { contextModuleName: string; contextTabId: string } {
      // Fall back to the legacy tab index when the module-specific tab context is absent.
      const [contextModuleName = moduleName ?? '', contextTabId = urlContext.t ?? '0'] = urlContext.tm?.split(':') ?? []

      return { contextModuleName, contextTabId }
    }

    function updateTabContext(current: UrlContext, moduleName: string | undefined, tabId: string) {
      current['tm'] = `${moduleName}:${tabId}`
    }

    return {
      context: urlContext,
      update,
      getTabContext,
      updateTabContext,
      serialize,
      deserializeUrl,
    }
  }, [urlContext, setUrlContext, buId])
}
